import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import throttle from 'lodash/throttle';
import React, {Component, createRef} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {Helmet} from 'react-helmet-async';
import moment from 'moment';
import {getWindow} from 'ssr-window';
import IconArrowLeft from '@spothero/icons/arrow-left';
import IconTimes from '@spothero/icons/times';
import Alert from '@spothero/ui/alert';
import Button from '@spothero/ui/button';
import TextButton from '@spothero/ui/text-button';
import DOMUtils from '@spothero/utils/dom';
import FacilitySummary from 'common/facility-details/FacilitySummary';
import {push} from 'router/router-actions';
import trackAvailabilityCheck from 'segment/events/availability-check';
import trackSpotDetails from 'segment/pages/spot-details';
import {cancelSpotRequests} from 'spots/spots-actions';
import DateUtils from 'utils/date';
import GTMUtils from 'utils/gtm';
import SegmentUtils from 'utils/segment';
import SpotUtils from 'utils/spot';
import {retrieveSpotTravelDistance} from 'utils/travel-distance';
import ParkingOptionsMonthly from './reservation-details/ParkingOptionsMonthly';
import SpotDetailsReservationSummary from './reservation-details/SpotDetailsReservationSummary';
import ParkingRestrictionsMonthly from './reservation-details/ParkingRestrictionsMonthly';
import AccessHours from './access/AccessHours';
import AmenitiesList from './details/AmenitiesList';
import SpotDetailsPlaceholder from './details/SpotDetailsPlaceholder';
import SpotImages from './details/SpotImages';
import SpotNotes from './details/SpotNotes';
import ThingsYouShouldKnowMonthly from './details/ThingsYouShouldKnowMonthly';
import GettingThere from './getting-there/GettingThere';
import RateDetailsModal from './modals/RateDetailsModal';
import HowToRedeem from './redeem/HowToRedeem';
import Reviews from './reviews/Reviews';
import PaymentMethodsIcons from 'common/payment-methods-icons/PaymentMethodsIcons';
import FAQPanels from './footer/FAQPanels';
import TrustSealValueProps from './footer/TrustSealValueProps';

const window = getWindow();

class SpotDetailsMonthly extends Component {
    static propTypes = {
        isMobile: PropTypes.bool,
        user: PropTypes.object,
        spotId: PropTypes.number,
        city: PropTypes.object,
        destination: PropTypes.object,
        event: PropTypes.object,
        searchRequest: PropTypes.object,
        referrerAffiliate: PropTypes.string,
        isLowInventoryEnabled: PropTypes.bool,
        onCloseClick: PropTypes.func.isRequired,
        cancelAllSpotRequests: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
    };
    _container = createRef();

    constructor(props) {
        super(props);

        this.state = {
            updatedSpotData: null,
            isShowing: false,
            isLoadingRates: true,
            isRateLoadError: false,
            showSpotUnavailable: false,
            showRateDetailsModal: false,
            rateDetailsSection: null,
            showStickySpotDetailsReservationSummary: false,
        };
    }

    componentDidMount() {
        DOMUtils.addClass('html', 'SpotDetails-open');

        this._initSpotDetails();

        window.addEventListener('keydown', this._onKeyDown);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.spotId !== this.props.spotId) {
            this._initSpotDetails();
        }
    }

    componentWillUnmount() {
        this._isUnmounted = true;

        DOMUtils.removeClass('html', 'SpotDetails-open');

        if (this._rateLoader) {
            this._rateLoader.cancel();
            this._rateLoader = null;
        }

        window.removeEventListener('keydown', this._onKeyDown);
    }

    _onKeyDown = evt => {
        // close spot details if escape is pressed on keyboard
        if (evt.which === 27) {
            this._onCloseClick();
        }
    };

    _onRetryClick = evt => {
        this._loadRates();
    };

    _onScroll = () => {
        const {isLoadingRates, isRateLoadError} = this.state;

        if (isLoadingRates || isRateLoadError) {
            return;
        }

        const summaryEl = DOMUtils.el('.SpotDetailsReservationSummary')[0];
        const {top: summaryTop} = DOMUtils.getDocumentOffset(summaryEl);
        const summaryHeight = DOMUtils.height(summaryEl);
        const headerHeight = DOMUtils.height('.Header');
        const summaryBottom = summaryTop + summaryHeight;

        this.setState({
            showStickySpotDetailsReservationSummary:
                summaryBottom < headerHeight,
        });
    };

    _onMultiRateChange = rateId => {
        const {searchRequest} = this.props;
        const {updatedSpotData: spotData} = this.state;
        const rateType = 'monthly_rates';
        const newRate = find(spotData[rateType], {
            fullRule: parseInt(rateId, 10),
        });
        const newSpotData = {
            ...spotData,
            selectedRate: false,
        };

        if (newRate) {
            newRate.fullUrl = SpotUtils.buildCheckoutURL(
                newSpotData,
                newRate,
                searchRequest
            );
            newSpotData.selectedRate = newRate;
        }

        this.setState({
            updatedSpotData: newSpotData,
        });
    };

    _onShowRateDetailsClick = section => {
        this.setState({
            showRateDetailsModal: true,
            rateDetailsSection: isString(section) ? section : null,
        });
    };

    _onBookSpotClick = evt => {
        evt.preventDefault();

        const {cancelAllSpotRequests, pushTo} = this.props;
        const {updatedSpotData: spotData} = this.state;

        if (!spotData.selectedRate) {
            return null;
        }

        const {
            selectedRate: {fullUrl},
        } = spotData;

        cancelAllSpotRequests();

        pushTo(fullUrl);
    };

    _onHideAllModals = () => {
        this.setState({
            showRateDetailsModal: false,
        });
    };

    _onCloseClick = () => {
        const {isMobile, onCloseClick} = this.props;
        const node = this._container.current;

        this.setState({
            isShowing: false,
            showStickySpotDetailsReservationSummary: false,
        });

        if (isMobile) {
            onCloseClick();
        } else {
            window.transitionEnd(node).bind(() => {
                window.transitionEnd(node).unbind();

                onCloseClick();
            });
        }
    };

    _initSpotDetails() {
        this._loadRates();

        this.setState({
            isShowing: true,
        });

        SegmentUtils.track({
            event: 'opened_spot_details',
        });
    }

    _trackSegmentEvents(spot) {
        const {
            user: {id: userId, status: userStatus},
            searchRequest,
            referrerAffiliate,
            city,
            destination,
            event,
        } = this.props;
        const {monthly: isMonthly, starts, ends, referrer} = searchRequest;

        trackSpotDetails({
            userId,
            city,
            destination,
            event,
            isMonthly,
            starts,
            ends,
            spot,
            referrer,
            referrerAffiliate,
        });

        trackAvailabilityCheck({
            searchRequest,
            city,
            destination,
            event,
            spot,
            userStatus,
            screen: 'spot details',
        });
    }

    _loadRates() {
        const {spotId, searchRequest, city, event} = this.props;
        const {monthly: isMonthly, starts, ends} = searchRequest;
        const eventId = event ? event.id : null;

        this.setState({
            isLoadingRates: true,
            isRateLoadError: false,
        });

        this._rateLoader = SpotUtils.loadRates({
            spotId,
            starts,
            ends,
            isMonthly,
            timezone: city.timezone,
            searchDataParams: {
                monthly: isMonthly,
                starts,
                ends,
                eid: eventId,
            },
            spot: null,
            eventId,
            onSuccessWithRates: updatedSpotData => {
                this.setState({
                    updatedSpotData,
                    showSpotUnavailable: false,
                    isLoadingRates: false,
                });
                this._trackSegmentEvents(updatedSpotData);
                this._rateLoader = null;

                retrieveSpotTravelDistance(updatedSpotData)
                    .then(spotWithTravelDistance => {
                        this.setState({
                            updatedSpotData: spotWithTravelDistance[0],
                        });
                    })
                    .catch(error => {
                        // silently catch this error to ignore errors being logged
                    });
            },
            onSuccessWithoutRates: partialSpotData => {
                GTMUtils.push({
                    event: 'GAEvent',
                    eventCategory: 'automatic',
                    eventAction: 'rate_unavailable',
                    eventLabel: 'monthly_rate_toggle',
                    eventValue: undefined, // eslint-disable-line no-undefined
                    hitCallback: undefined, // eslint-disable-line no-undefined
                });

                this.setState({
                    updatedSpotData: partialSpotData,
                    showSpotUnavailable: true,
                    isLoadingRates: false,
                });
                retrieveSpotTravelDistance(partialSpotData)
                    .then(spotWithTravelDistance => {
                        this.setState({
                            updatedSpotData: spotWithTravelDistance[0],
                        });
                    })
                    .catch(error => {
                        // silently catch this error to ignore errors being logged
                    });
                this._rateLoader = null;
            },
            onError: error => {
                if (this._isUnmounted) {
                    return;
                }

                this.setState({
                    updatedSpotData: null,
                    showSpotUnavailable: true,
                    isLoadingRates: false,
                    isRateLoadError: true,
                });
                this._rateLoader = null;
            },
        });
    }

    _renderSpotImages(facility) {
        const {isMobile} = this.props;
        const {
            parking_spot_id: facilityId,
            title: facilityTitle,
            images: facilityImages,
        } = facility;

        return (
            <SpotImages
                key={facilityId}
                images={facilityImages}
                alt={facilityTitle}
                showArrows={!isMobile}
            />
        );
    }

    _renderFacilitySummary(spot) {
        const {
            user: {isAdmin},
            isLowInventoryEnabled,
        } = this.props;
        const {available_monthly_inventory: monthlyInventory} = spot;
        const inventory = monthlyInventory?.default_rate;

        return (
            <FacilitySummary
                isAdmin={isAdmin}
                spot={spot}
                priceLabel="/month"
                availableInventory={inventory}
                lowInventoryThreshold={3}
                showImages={false}
                showPrice={false}
                showFullTitle
                isLowInventoryEnabled={isLowInventoryEnabled}
                showAdminLink
            />
        );
    }

    _renderParkingOptions(spot) {
        const {selectedRate, monthly_rates: monthlyRates} = spot;

        return (
            <ParkingOptionsMonthly
                availableRates={monthlyRates}
                selectedRate={selectedRate}
                onMultiRateChange={this._onMultiRateChange}
                onRateDetailsClick={this._onShowRateDetailsClick}
            />
        );
    }

    _renderMonthlyParkingRestrictions(selectedRate) {
        const {introductory} = selectedRate;
        const hasIntroRate = Boolean(introductory && introductory.rate);

        return (
            <ParkingRestrictionsMonthly
                hasIntroRate={hasIntroRate}
                onRateDetailsClick={this._onShowRateDetailsClick}
            />
        );
    }

    _renderReservationSummary(spot, isSticky, isStickyVersionShowing) {
        const {isMobile} = this.props;
        const {isLoadingRates, showSpotUnavailable} = this.state;
        const {selectedRate} = spot;
        let displayPrice = null;
        let currencyType = null;
        let reservationDateTimeFormatted = '';

        if (selectedRate) {
            displayPrice = selectedRate.display_price;
            currencyType = selectedRate.currency_type;

            reservationDateTimeFormatted = DateUtils.getDateTimeSummary({
                startDateTimeObj: moment(selectedRate.starts),
                endDateTimeObj: moment(selectedRate.ends),
                isMonthly: true,
            });
        }

        return (
            <SpotDetailsReservationSummary
                reservationLabel="Parking Starting"
                priceLabel="/month"
                isMobile={isMobile}
                isLoadingRates={isLoadingRates}
                displayPrice={displayPrice}
                currencyType={currencyType}
                reservationDateTimeFormatted={reservationDateTimeFormatted}
                isReservationExtended={false}
                showSpotUnavailable={showSpotUnavailable}
                onBookSpotClick={this._onBookSpotClick}
                isSticky={isSticky}
                isShowing={isStickyVersionShowing}
            />
        );
    }

    _renderThingsYouShouldKnowMonthly(spot) {
        const {selectedRate, facility} = spot;
        const {
            height_restriction_description: heightRestrictionDescription,
        } = facility;
        const {
            contract: {required: contractRequired},
            in_out: inOut,
            parking_pass_type: parkingPassType,
            new_description: newRateDescription, // there is a description field that no longer seems to be used
            newMonthlyRate, // this is set in spot.js, we should revisit in search v2
        } = selectedRate;

        return (
            <ThingsYouShouldKnowMonthly
                isNewMonthlyRate={newMonthlyRate}
                heightRestrictionDescription={heightRestrictionDescription}
                contractRequired={contractRequired}
                inOut={inOut}
                parkingPassType={parkingPassType}
                rateDescription={newRateDescription}
                onShowRateDetailsClick={this._onShowRateDetailsClick}
            />
        );
    }

    _renderAmenities(spot) {
        const {selectedRate, facility} = spot;
        const {amenities_full: nonRateAmenities} = facility;

        return (
            <AmenitiesList
                rate={selectedRate}
                isMonthly
                nonRateAmenities={nonRateAmenities}
                onShowRateDetailsClick={this._onShowRateDetailsClick}
            />
        );
    }

    _renderAccessHours(spot) {
        const {selectedRate, facility} = spot;
        const {access_hours_formatted: facilityAccessHours} = facility;
        const {
            access_hours_formatted: rateAccessHours,
            display_hours_comment: hoursComment,
        } = selectedRate;
        const showAccessHours = Boolean(
            rateAccessHours || hoursComment || !isEmpty(facilityAccessHours)
        );

        return (
            showAccessHours && (
                <AccessHours
                    rate={selectedRate}
                    facility={facility}
                    sectionTitle="Access Hours"
                />
            )
        );
    }

    _renderRedemptionInstructions(selectedRate) {
        const {redemption_instructions: rateInstructions} = selectedRate;
        const hasRedemptionInstructions = Boolean(rateInstructions.length);

        return (
            hasRedemptionInstructions && (
                <HowToRedeem
                    sectionTitle="How to Redeem"
                    instructions={rateInstructions}
                />
            )
        );
    }

    _renderGettingThere(spot) {
        const {facility, selectedRate} = spot;
        const {getting_here: gettingHere} = facility;
        const gettingThereInstructions = selectedRate.getting_here
            ? selectedRate.getting_here
            : gettingHere;

        return (
            gettingThereInstructions && (
                <GettingThere instructions={gettingThereInstructions} />
            )
        );
    }

    _renderReviews(facility) {
        const {rating_info: ratingInfo} = facility;

        return <Reviews ratingInfo={ratingInfo} />;
    }

    _renderSpotNotes(spot) {
        const {operator_notes: operatorNotes, spot_notes: spotNotes} = spot;

        return (
            <>
                <div className="SpotDetails-section">
                    <h4 className="subtitle">Spot Notes (Admin)</h4>
                    <SpotNotes type="spot" notes={spotNotes} />
                </div>
                <div className="SpotDetails-section">
                    <h4 className="subtitle">Operator Notes (Admin)</h4>
                    <SpotNotes type="operator" notes={operatorNotes} />
                </div>
            </>
        );
    }

    _renderFooter(facility) {
        const {cancellation_allowed: cancellationAllowed} = facility;

        return (
            <div className="SpotDetails-footer">
                <FAQPanels cancellationAllowed={cancellationAllowed} />
                <TrustSealValueProps />
            </div>
        );
    }

    render() {
        const {
            isMobile,
            user: {isAdmin},
        } = this.props;
        const {
            updatedSpotData: spot,
            isShowing,
            isLoadingRates,
            isRateLoadError,
            rateDetailsSection,
            showStickySpotDetailsReservationSummary,
            showRateDetailsModal,
        } = this.state;
        const classes = classNames('SpotDetails', {
            'SpotDetails-showing': isShowing,
        });
        const shimClasses = classNames('SpotDetails-shim', 'shim', {
            'SpotDetails-shim-showing': isShowing,
        });
        // These booleans are used to conditionally display subcomponents
        // Some subcomponents are rate-dependent, while others can be displayed if no rate is available
        const hasFacilityData = Boolean(spot);
        const hasFacilityAndRateData = Boolean(spot?.selectedRate);

        return (
            <>
                <Helmet>
                    <meta name="robots" content="noindex, follow" />
                </Helmet>
                <div className={shimClasses} onClick={this._onCloseClick} />
                <div
                    ref={this._container}
                    className={classes}
                    onScroll={throttle(this._onScroll, 500)}
                >
                    {isMobile ? (
                        <Button
                            className="SpotDetails-back"
                            onClick={this._onCloseClick}
                        >
                            {/* span allows CSS to create larger tap target for this button */}
                            <span>
                                <IconArrowLeft />
                            </span>
                        </Button>
                    ) : (
                        <div className="SpotDetails-close-container">
                            <Button
                                className="SpotDetails-close"
                                onClick={this._onCloseClick}
                            >
                                <IconTimes />
                            </Button>
                        </div>
                    )}
                    {hasFacilityData && this._renderSpotImages(spot.facility)}
                    {hasFacilityData && this._renderFacilitySummary(spot)}
                    {hasFacilityAndRateData && this._renderParkingOptions(spot)}
                    {hasFacilityAndRateData &&
                        this._renderMonthlyParkingRestrictions(
                            spot.selectedRate
                        )}
                    {hasFacilityData &&
                        this._renderReservationSummary(spot, false, false)}
                    {hasFacilityData && (
                        <PaymentMethodsIcons showAppleAndGooglePay />
                    )}
                    {isLoadingRates && <SpotDetailsPlaceholder />}
                    {isRateLoadError && (
                        <Alert className="SpotDetails-rate-error" type="danger">
                            There was an error retrieving the facility
                            information.{' '}
                            <TextButton onClick={this._onRetryClick}>
                                Retry
                            </TextButton>
                        </Alert>
                    )}
                    {hasFacilityAndRateData &&
                        this._renderThingsYouShouldKnowMonthly(spot)}
                    {hasFacilityAndRateData && this._renderAmenities(spot)}
                    {hasFacilityAndRateData && this._renderAccessHours(spot)}
                    {hasFacilityAndRateData &&
                        this._renderRedemptionInstructions(spot.selectedRate)}
                    {hasFacilityAndRateData && this._renderGettingThere(spot)}
                    {hasFacilityData && this._renderReviews(spot.facility)}
                    {isAdmin && hasFacilityData && this._renderSpotNotes(spot)}
                    {hasFacilityData && this._renderFooter(spot.facility)}
                    {hasFacilityData &&
                        this._renderReservationSummary(
                            spot,
                            true,
                            showStickySpotDetailsReservationSummary
                        )}
                </div>
                {showRateDetailsModal && (
                    <RateDetailsModal
                        spot={spot}
                        section={rateDetailsSection}
                        onHidden={this._onHideAllModals}
                    />
                )}
            </>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {data: city},
        destination: {data: destination},
        event: {data: event},
        user: {data: user},
        search: {
            data: {referrerAffiliate},
        },
        searchRequest,
    } = state;

    return {
        city,
        destination,
        event,
        user,
        referrerAffiliate,
        searchRequest,
    };
};

const mapDispatchToProps = {
    cancelAllSpotRequests: cancelSpotRequests,
    pushTo: push,
};

export default connect(mapStateToProps, mapDispatchToProps)(SpotDetailsMonthly);
